import * as React from "react"
import { useEffect, useState } from "react"
import { Box, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material"
import { ButtonLoadingSeeker } from "@ui-seeker/core"
import { AutocompleteAsyncDebounced } from "@components"
import { useAppToast } from "@ui-seeker/app"
import { postSubmit } from "@ui-seeker/utils"

import { BACKEND_APIS } from "@constants"
import { multipleRowsSelected, multipleRowsSent, tableData } from "../@signals"


export default function ProductsReviewTableToolbar({ selectedData = [], setSelectedRows }) {
    const { showToast } = useAppToast()
    const [isRemoving, setIsRemoving] = useState(false)
    const [isValidating, setIsValidating] = useState(false)
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [assignBrandOpen, setAssignBrandOpen] = useState(false)
    const [selectedBrand, setSelectedBrand] = useState(null)
    const [isAssigningBrand, setIsAssigningBrand] = useState(false)

    // Expand grouped rows to individual products
    const expandedProducts = selectedData.flatMap(row =>
        row.raw?._groupedProducts || [row]
    )

    const handleMultipleEditClick = () => {
        multipleRowsSelected.value = expandedProducts
    }

    const handleValidateClick = async () => {
        const unvalidatedRows = expandedProducts.filter(row =>
            row.sku && (row.sku.validated === 0 || row.sku.validated === false)
        )

        if (unvalidatedRows.length === 0) {
            showToast.warning('No unvalidated mappings selected')
            return
        }

        setIsValidating(true)
        try {
            const mappings = unvalidatedRows.map(row => ({
                id_site: row.raw?.id_site,
                product_id: row.raw?.product_id
            }))

            await postSubmit(`/api/products-review/validate`, { mappings })

            // Update local table data
            const validatedKeys = unvalidatedRows.map(row => `${row.raw?.product_id}-${row.raw?.id_site}`)
            tableData.value = tableData.value.map(item => {
                const key = `${item.raw?.product_id}-${item.raw?.id_site}`
                if (validatedKeys.includes(key)) {
                    return { ...item, sku: { ...item.sku, validated: 1 } }
                }
                return item
            })

            showToast.success(`${unvalidatedRows.length} mappings validated`)
            setSelectedRows([])
        } catch (error) {
            showToast.error('Error validating mappings')
        } finally {
            setIsValidating(false)
        }
    }

    const handleRemoveClick = () => {
        const rowsWithMapping = expandedProducts.filter(row => row.sku)
        if (rowsWithMapping.length === 0) {
            showToast.warning('No mapped products selected')
            return
        }
        setConfirmOpen(true)
    }

    const handleRemoveConfirmed = async () => {
        setConfirmOpen(false)

        // Filter only rows that have a mapping (sku exists)
        const rowsWithMapping = expandedProducts.filter(row => row.sku)

        setIsRemoving(true)
        try {
            // Send all remove requests in parallel
            const promises = rowsWithMapping.map(row =>
                postSubmit(`/api/mapping/sku`, {
                    productID: row.raw?.product_id,
                    siteID: row.raw?.id_site,
                    skuID: 0,
                    source: 'product-review-remove-multiple'
                })
            )

            await Promise.all(promises)

            // Update local table data
            const removedKeys = rowsWithMapping.map(row => `${row.raw?.product_id}-${row.raw?.id_site}`)
            tableData.value = tableData.value.map(item => {
                const key = `${item.raw?.product_id}-${item.raw?.id_site}`
                if (removedKeys.includes(key)) {
                    return { ...item, sku: null }
                }
                return item
            })

            showToast.success(`${rowsWithMapping.length} mappings removed`)
            setSelectedRows([])
        } catch (error) {
            showToast.error('Error removing mappings')
        } finally {
            setIsRemoving(false)
        }
    }

    const handleAssignBrandClick = () => {
        setSelectedBrand(null)
        setAssignBrandOpen(true)
    }

    const handleAssignBrandConfirmed = async () => {
        if (!selectedBrand) return

        setIsAssigningBrand(true)
        try {
            const mappings = expandedProducts.map(row => ({
                id_site: row.raw?.id_site,
                product_id: row.raw?.product_id
            }))

            await postSubmit(`${BACKEND_APIS.PRODUCTS_REVIEW}/assign-brand`, {
                mappings,
                id_brand: selectedBrand.value
            })

            // Update local table data
            const affectedKeys = expandedProducts.map(row => `${row.raw?.product_id}-${row.raw?.id_site}`)
            tableData.value = tableData.value.map(item => {
                const key = `${item.raw?.product_id}-${item.raw?.id_site}`
                if (affectedKeys.includes(key)) {
                    return {
                        ...item,
                        raw: { ...item.raw, id_brand: selectedBrand.value, brand: selectedBrand.label }
                    }
                }
                return item
            })

            showToast.success(`Brand assigned to ${expandedProducts.length} products`)
            setSelectedRows([])
            setAssignBrandOpen(false)
        } catch (error) {
            showToast.error('Error assigning brand')
        } finally {
            setIsAssigningBrand(false)
        }
    }

    // Clear selection when mapping is done
    useEffect(() => {
        if (multipleRowsSent.value.length > 0) {
            setSelectedRows([])
            multipleRowsSent.value = []
        }
    }, [multipleRowsSent.value.length])

    const mappedCount = expandedProducts.filter(row => row.sku).length
    const unvalidatedCount = expandedProducts.filter(row =>
        row.sku && (row.sku.validated === 0 || row.sku.validated === false)
    ).length
    const totalProductCount = expandedProducts.length

    return (
        <>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, mr: 2.5 }}>
                <ButtonLoadingSeeker
                    icon="pen-to-square"
                    tooltip="Edit mapping for selected products"
                    onClick={handleMultipleEditClick}
                >
                    Edit Multiple ({totalProductCount})
                </ButtonLoadingSeeker>
                <ButtonLoadingSeeker
                    icon="tag"
                    tooltip="Assign brand to selected products"
                    onClick={handleAssignBrandClick}
                    isLoading={isAssigningBrand}
                    color="info"
                >
                    Assign Brand ({totalProductCount})
                </ButtonLoadingSeeker>
                {unvalidatedCount > 0 && (
                    <ButtonLoadingSeeker
                        icon="check-circle"
                        tooltip="Validate selected mappings"
                        onClick={handleValidateClick}
                        isLoading={isValidating}
                        color="warning"
                    >
                        Validate ({unvalidatedCount})
                    </ButtonLoadingSeeker>
                )}
                {mappedCount > 0 && (
                    <ButtonLoadingSeeker
                        icon="link-slash"
                        tooltip="Remove mapping from selected products"
                        onClick={handleRemoveClick}
                        isLoading={isRemoving}
                        color="error"
                    >
                        Remove ({mappedCount})
                    </ButtonLoadingSeeker>
                )}
            </Box>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirm Remove Mappings</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to remove mappings from {mappedCount} product{mappedCount !== 1 ? 's' : ''}?
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={handleRemoveConfirmed} color="error" variant="contained">
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={assignBrandOpen}
                onClose={() => setAssignBrandOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Assign Brand</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Select a brand to assign to {totalProductCount} selected product{totalProductCount !== 1 ? 's' : ''}.
                    </DialogContentText>
                    <AutocompleteAsyncDebounced
                        urlAjax={`${BACKEND_APIS.BRANDS}/crud/brand/find-options`}
                        label="Brand"
                        placeholder="Search Brands..."
                        value={selectedBrand?.value || null}
                        onChange={(e, op) => setSelectedBrand(op || null)}
                        queryParam="search"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignBrandOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleAssignBrandConfirmed}
                        variant="contained"
                        disabled={!selectedBrand || isAssigningBrand}
                    >
                        Assign
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
